import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { from, Observable, pipe } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { appConfigService } from '@app/config';
import { AuthRequestOptions, BackendUploadService } from '@ntap/core';

@Injectable({
  providedIn: 'root'
})
export class ContentDownloadService {
  private config = inject(appConfigService);
  private http = inject(HttpClient);
  private backendUploadService = inject(BackendUploadService);
  private baseUrl: string;
  private readonly CONCURRENCY = 3;

  constructor() {
    this.baseUrl = this.config.apiHost;
  }

  private doDownload = pipe(
    map((response: HttpResponse<any>) => this.extractResponseBlob(response)),
    tap((data) => this.downloadLink(data))
  );

  downloadFiles(
    files: { objectType: string; isFolder: boolean; objectId: any }[],
    renditionName = ''
  ) {
    const downloadTasks = files.map((file) => {
      return {
        objectType: file.objectType,
        file: file,
        renditionName: renditionName
      };
    });
    return from(downloadTasks).pipe(
      mergeMap((task) => {
        if (task.file.isFolder) {
          return this.downloadFolder(
            task.objectType,
            task.file.objectId,
            task.renditionName
          );
        } else {
          return this.downloadFile(
            task.objectType,
            task.file.objectId,
            task.renditionName
          );
        }
      }, this.CONCURRENCY)
    );
  }

  downloadFile(
    objectType: string,
    objectId: string,
    renditionName = ''
  ): Observable<any> {
    return this.backendUploadService
      .readContent(objectType, objectId, renditionName)
      .pipe(tap((data) => this.downloadLink(data)));
  }

  /**
   * Prikaz u tabu ovisi o Content-Type headeru koji vraća backend
   * (npr. application/pdf, image/*, text/* se prikazuju; ostalo se preuzima).
   */
  openFileInNewTab(
    objectType: string,
    objectId: string,
    renditionName = ''
  ): Observable<any> {
    return this.backendUploadService
      .readContent(objectType, objectId, renditionName)
      .pipe(tap((data) => this.openInNewTab(data)));
  }

  downloadFolder(
    objectType: string,
    objectId: string,
    renditionName = ''
  ): Observable<any> {
    const url = `${
      this.baseUrl
    }api/ntap/ui/readContentZip?objectType=${objectType.replace(
      '.',
      '~'
    )}&objectId=${objectId}&renditionName=${renditionName}`;
    const options = new AuthRequestOptions();
    return this.http
      .get(url, { ...options, responseType: 'blob', observe: 'response' })
      .pipe(this.doDownload);
  }

  private downloadLink(data: { file: Blob; filename: string }) {
    const element = document.createElement('a');
    element.href = URL.createObjectURL(data.file);
    element.download = data.filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  private openInNewTab(data: { file: Blob; filename: string }) {
    const blobUrl = URL.createObjectURL(data.file);
    const newWindow = window.open(blobUrl, '_blank');
    if (!newWindow) {
      // Preglednik je blokirao popup - fallback na preuzimanje
      this.downloadLink(data);
      URL.revokeObjectURL(blobUrl);
      return;
    }
    // Oslobodi memoriju nakon što se tab učita
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  }

  private extractResponseBlob(response: HttpResponse<any>): {
    file: Blob;
    filename: string;
  } {
    const contentDisposition = response.headers.get('Content-Disposition');
    if (!contentDisposition) {
      throw new Error('Content-Disposition header not sent in response!');
    }
    const filenameRegex = /filename\*?=(?:(['"])(.*?)\1|([^;]+))/i; // either quoted or unquoted up to the ;
    const matches = filenameRegex.exec(contentDisposition);
    let filename = '';
    if (matches && (matches[2] || matches[3])) {
      const encodedFilename = matches[2] || matches[3];
      filename = decodeURIComponent(encodedFilename.replace(/UTF-8''/, ''));
    } else {
      throw new Error(
        'Filename not sent properly in Content-Disposition header!'
      );
    }

    const mimeType = response.headers.get('Content-Type');
    if (!mimeType) {
      throw new Error('Content-Type header not sent in response!');
    }
    const data = {
      file: new Blob([response.body], { type: mimeType }),
      filename: filename
    };
    return data;
  }
}
